import { Box, Small, P, FlexItem } from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import themeGet from "@styled-system/theme-get";

import { TreeHeader, TreeHeaderTitle } from "src/shared/TreeView/TreeView";
import { RichTextContentViewer } from "src/shared/RichTextContent/RichTextContent";
import { isTagRouteActive } from "src/util/tagRouteActive";

/** Same layout as TreeHeaderTitle but a real anchor for copy-link / open-in-new-tab */
const TagTreeHeaderTitleLink = styled.a`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  justify-content: space-between;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: ${({ isHidden }) => (isHidden ? "line-through" : "none")};
  color: inherit;
  margin-left: 0;
  line-height: 1;
  padding: 0;
  padding-left: ${(props) =>
    props.hasChildren ? "0px" : `calc(24px + ${themeGet("space.s")(props)})`};
  min-width: 0;
  flex: 1;
  overflow: hidden;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

const TagTreeHeaderTitleRouterLink = styled(Link)`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  justify-content: space-between;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: none;
  color: inherit;
  margin-left: 0;
  line-height: 1;
  padding: 0;
  padding-left: ${(props) =>
    props.$hasChildren ? "0px" : `calc(24px + ${themeGet("space.s")(props)})`};
  min-width: 0;
  flex: 1;
  overflow: hidden;

  &:hover,
  &:focus {
    color: inherit;
  }
`;

const TreeText = styled(P)`
  word-break: break-word;
  z-index: 2;
  transition: ${themeGet("transition.transitionDefault")};
  color: ${(props) =>
    props.isActive
      ? themeGet("colors.primary")(props)
      : themeGet("colors.bodyText")(props)};

  ${TreeHeader}:hover & {
    color: ${(props) => themeGet("colors.primary")(props)};
  }
`;

const TagTreeHeader = ({
  node,
  onActivate,
  getTagLinkTo,
  onToggle: _onToggle,
  activeTagId,
  tagTypesConfig,
  sectionTagTypeId,
}) => {
  const { isTagType, isTagTypeDesc, name, tagValue } = node;
  const isActive = isTagRouteActive(
    node,
    activeTagId,
    tagTypesConfig,
    sectionTagTypeId
  );

  if (isTagType) {
    const hasChildren = Boolean(node.children);

    const tagTypeHref =
      typeof getTagLinkTo === "function" ? getTagLinkTo(node) : null;

    const tagTypeTitle = (
      <TreeText fontSize="1" isActive={isActive}>
        {name}
      </TreeText>
    );

    const onClickTagType = (e) => {
      e.stopPropagation();
      onActivate(node);
    };

    return (
      <TreeHeader
        onClick={tagTypeHref ? (e) => e.stopPropagation() : onClickTagType}
        isActive={isActive}
      >
        {tagTypeHref ? (
          <TagTreeHeaderTitleRouterLink
            className="treeHeaderTitle"
            data-testid="treeheader-node-name"
            to={tagTypeHref}
            $hasChildren={hasChildren}
            style={{ minWidth: 0 }}
            onClick={onClickTagType}
          >
            {tagTypeTitle}
          </TagTreeHeaderTitleRouterLink>
        ) : (
          <TreeHeaderTitle
            data-testid="treeheader-node-name"
            hasChildren={hasChildren}
            hasToggle={hasChildren}
          >
            {tagTypeTitle}
          </TreeHeaderTitle>
        )}
      </TreeHeader>
    );
  }

  if (isTagTypeDesc) {
    return (
      <Box ml="l" dataTestId="tag-type-desc-node">
        <RichTextContentViewer content={node.details} fontSize="1.2rem" />
      </Box>
    );
  }

  const { appliedCount } = tagValue || {};
  const hasChildren = Boolean(node.children);

  const onClickTag = (e) => {
    e.stopPropagation();
    onActivate(node);
  };

  const tagHref =
    typeof getTagLinkTo === "function" ? getTagLinkTo(node) : null;

  const tagValueTitle = (
    <>
      <TreeText fontSize="1" isActive={isActive}>
        {name}
      </TreeText>
      <FlexItem flex="0 0 auto">
        <Small color="subText">
          {appliedCount ? `${appliedCount} tagged` : ""}
        </Small>
      </FlexItem>
    </>
  );

  return (
    <TreeHeader onClick={onClickTag} isActive={isActive}>
      {tagHref ? (
        <TagTreeHeaderTitleLink
          className="treeHeaderTitle"
          data-testid="treeheader-node-name"
          href={tagHref}
          hasChildren={hasChildren}
          style={{ minWidth: 0 }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClickTag(e);
          }}
        >
          {tagValueTitle}
        </TagTreeHeaderTitleLink>
      ) : (
        <TreeHeaderTitle
          data-testid="treeheader-node-name"
          hasChildren={hasChildren}
          hasToggle={hasChildren}
        >
          {tagValueTitle}
        </TreeHeaderTitle>
      )}
    </TreeHeader>
  );
};

TagTreeHeader.propTypes = {
  node: PropTypes.object,
  onToggle: PropTypes.func,
  onActivate: PropTypes.func,
  getTagLinkTo: PropTypes.func,
  activeTagId: PropTypes.string,
  tagTypesConfig: PropTypes.object,
  sectionTagTypeId: PropTypes.string,
};

export default TagTreeHeader;
